import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { type ReactNode, useState } from "react";

import type { Payer } from "graphql_globals";
import { RadioGroup, RadioInput, RadioLabel } from "common/core/form/option";

import { CardForm } from "../card_form";
import { AchForm } from "../ach_form";
import Styles from "./index.module.scss";
import type { Payment_node_Organization_defaultPaymentSource } from "../../graphql/payment_query.graphql";

const MESSAGES = defineMessages({
  creditCard: {
    id: "1784d6f1-855c-4e5a-9117-e2283007e295",
    description: "creditCard",
    defaultMessage: "Credit Card",
  },
  bankAccount: {
    id: "6a09ca9f-6dea-437b-9b0c-04fe5f73011e",
    description: "bankAccount",
    defaultMessage: "Bank Account",
  },
});

export type PaymentSource = Payment_node_Organization_defaultPaymentSource | null;

type PaymentFormType = {
  currentPaymentSource: PaymentSource | null;
  organizationId: string;
  payer: Payer;
  onChangesSaved: () => void;
  submitButtonLabel?: ReactNode;
};

enum PaymentSourceEnum {
  "AchAccount",
  "Card",
}

export function PaymentInlineForm({
  currentPaymentSource,
  organizationId,
  payer,
  onChangesSaved,
  submitButtonLabel,
}: PaymentFormType) {
  const intl = useIntl();
  const [radioButtonSelected, setRadioButtonSelected] = useState<PaymentSourceEnum>(
    currentPaymentSource?.type === "AchAccount"
      ? PaymentSourceEnum.AchAccount
      : PaymentSourceEnum.Card,
  );

  const showExistingCard =
    radioButtonSelected === PaymentSourceEnum.Card &&
    currentPaymentSource?.type === "Card" &&
    currentPaymentSource.name &&
    currentPaymentSource.last4;

  return (
    <div className={Styles.rowContainer}>
      <div className={Styles.row}>
        <span className={Styles.rowTitle}>Payment Method</span>

        <RadioGroup aria-labelledby={"payment-type-radio-group"} horizontal>
          <RadioLabel
            label={intl.formatMessage(MESSAGES.creditCard)}
            radio={
              <RadioInput
                value={PaymentSourceEnum.Card.toString()}
                checked={radioButtonSelected === PaymentSourceEnum.Card}
                onClick={() => setRadioButtonSelected(PaymentSourceEnum.Card)}
              />
            }
          />
          <RadioLabel
            label={intl.formatMessage(MESSAGES.bankAccount)}
            radio={
              <RadioInput
                value={intl.formatMessage(MESSAGES.bankAccount).toString()}
                checked={radioButtonSelected === PaymentSourceEnum.AchAccount}
                onClick={() => setRadioButtonSelected(PaymentSourceEnum.AchAccount)}
              />
            }
          />
        </RadioGroup>
      </div>

      {showExistingCard && (
        <div className={Styles.row}>
          <span className={Styles.rowTitle}>Current card</span>
          <span data-automation-id="current-card-field">{`${currentPaymentSource.name}, •••• •••• •••• ${currentPaymentSource.last4}`}</span>
        </div>
      )}
      {showExistingCard && (
        <h3 className={Styles.profileSectionHeader}>
          <FormattedMessage id="f8a54447-a942-4e1a-9734-b443d8d11a53" defaultMessage="New Card" />
        </h3>
      )}

      {radioButtonSelected === PaymentSourceEnum.Card && (
        <CardForm
          cardDetails={currentPaymentSource}
          organizationId={organizationId}
          payer={payer}
          onChangesSaved={onChangesSaved}
          submitButtonLabel={submitButtonLabel}
        />
      )}

      {radioButtonSelected === PaymentSourceEnum.AchAccount && (
        <AchForm
          paymentSource={currentPaymentSource}
          organizationId={organizationId}
          tooltipDescription
        />
      )}
    </div>
  );
}
